import React, { useEffect, useMemo, useState } from 'react';
import GenericModal from '../../../components/agv-generic-modal';
import ConsignmentRuleFormFields, {
  buildConsignmentRulePayload,
  isConsignmentRuleFormValid,
} from './consignment-rule-form-fields';
import {
  CONSIGNMENT_RULE_TYPES,
  CONSIGNMENT_DEFAULT_WEIGHT_UNIT,
  formatRuleNumber,
  fixedQuantityToProductsPerPallet,
} from '../../../utils/consignment-rule.util';

export default function ConsignmentRuleUpdateModal({
  isOpen,
  setIsOpen,
  updateConsignmentRule,
  mainActionIsLoading,
  archiveConsignmentRule,
  archiveIsLoading,
  rule,
  packagingOptions,
  packagingItems = [],
}) {
  const [itemKey, setItemKey] = useState('');
  const [packagingId, setPackagingId] = useState(null);
  const [ruleType, setRuleType] = useState(CONSIGNMENT_RULE_TYPES.LINE_DIRECT);
  const [productsPerPallet, setProductsPerPallet] = useState('');
  const [weightDivisor, setWeightDivisor] = useState('');
  const [weightUnit, setWeightUnit] = useState(CONSIGNMENT_DEFAULT_WEIGHT_UNIT);
  const [isActive, setIsActive] = useState('true');

  const isValid = useMemo(
    () =>
      isConsignmentRuleFormValid({
        itemKey,
        packagingId,
        ruleType,
        productsPerPallet,
        weightDivisor,
        weightUnit,
      }),
    [
      itemKey,
      packagingId,
      ruleType,
      productsPerPallet,
      weightDivisor,
      weightUnit,
    ]
  );

  const currentPayload = useMemo(
    () =>
      buildConsignmentRulePayload({
        itemKey,
        packagingId,
        ruleType,
        productsPerPallet,
        weightDivisor,
        weightUnit,
        isActive,
      }),
    [
      itemKey,
      packagingId,
      ruleType,
      productsPerPallet,
      weightDivisor,
      weightUnit,
      isActive,
    ]
  );

  const originalPayload = useMemo(() => {
    if (!rule) return null;
    return buildConsignmentRulePayload({
      itemKey: rule.item_key || '',
      packagingId: rule.packaging_id,
      ruleType: rule.rule_type,
      productsPerPallet: fixedQuantityToProductsPerPallet(rule.fixed_quantity),
      weightDivisor:
        rule.weight_divisor != null
          ? formatRuleNumber(rule.weight_divisor)
          : '',
      weightUnit: rule.weight_unit || CONSIGNMENT_DEFAULT_WEIGHT_UNIT,
      isActive: rule.is_active ? 'true' : 'false',
    });
  }, [rule]);

  const isSameValues = useMemo(() => {
    if (!originalPayload) return true;
    return JSON.stringify(currentPayload) === JSON.stringify(originalPayload);
  }, [currentPayload, originalPayload]);

  useEffect(() => {
    if (isOpen && rule) {
      setItemKey(rule.item_key || '');
      setPackagingId(rule.packaging_id ?? null);
      setRuleType(rule.rule_type || CONSIGNMENT_RULE_TYPES.LINE_DIRECT);
      setProductsPerPallet(
        fixedQuantityToProductsPerPallet(rule.fixed_quantity)
      );
      setWeightDivisor(
        rule.weight_divisor != null ? formatRuleNumber(rule.weight_divisor) : ''
      );
      setWeightUnit(rule.weight_unit || CONSIGNMENT_DEFAULT_WEIGHT_UNIT);
      setIsActive(rule.is_active ? 'true' : 'false');
    }
  }, [isOpen, rule]);

  const isLinkedPackagingInactive = useMemo(() => {
    if (!rule?.packaging_id || !packagingItems.length) return false;
    const linked = packagingItems.find((p) => p.id === rule.packaging_id);
    return linked ? !linked.is_active : false;
  }, [rule, packagingItems]);

  const handleSubmit = () => {
    updateConsignmentRule(rule?.id, currentPayload);
  };

  const handleArchive = () => {
    if (
      !window.confirm(
        'Archiver cette règle la supprimera définitivement de la liste. Confirmer ?'
      )
    )
      return;
    archiveConsignmentRule(rule?.id);
  };

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={'Modifier une règle de consigne'}
      mainAction={handleSubmit}
      mainActionText={'Modifier'}
      mainActionIsLoading={mainActionIsLoading}
      mainActionDisabled={!isValid || isSameValues}
      secondaryAction={handleArchive}
      secondaryActionText={'Archiver'}
      secondaryActionVariant={'secondary'}
      secondaryActionAlignLeft
      secondaryActionIsDisabled={archiveIsLoading || mainActionIsLoading}
      secondaryActionIsLoading={archiveIsLoading}
    >
      <ConsignmentRuleFormFields
        itemKey={itemKey}
        setItemKey={setItemKey}
        packagingId={packagingId}
        setPackagingId={setPackagingId}
        ruleType={ruleType}
        setRuleType={setRuleType}
        productsPerPallet={productsPerPallet}
        setProductsPerPallet={setProductsPerPallet}
        weightDivisor={weightDivisor}
        setWeightDivisor={setWeightDivisor}
        weightUnit={weightUnit}
        setWeightUnit={setWeightUnit}
        isActive={isActive}
        setIsActive={setIsActive}
        packagingOptions={packagingOptions}
        isLinkedPackagingInactive={isLinkedPackagingInactive}
      />
    </GenericModal>
  );
}
